import { useEffect, useId, useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  fetchMaterials,
  fetchUnits,
  type CatalogMaterial,
  type CatalogUnit,
} from '@client/api/catalog';
import { fetchRecentContext, saveEstimate } from '@client/api/estimates';
import { ApiError } from '@client/api/client';
import { calculateEstimate, calculateItem, calculateMaterialLine } from '@domain/calculation';
import { formatInr } from '@domain/currency';
import { NumericInput } from '@client/keypad/NumericInput';
import { useKeypadOverlayStore } from '@client/stores/keypad-overlay';
import { getItemNameSuggestions, rememberItemNames } from '@client/lib/item-name-cache';
import {
  getCachedRecentContext,
  mergeIntoRecentContext,
  setCachedRecentContext,
} from '@client/lib/recent-context-cache';
import { scrollFocusedToUpperThird } from '@client/lib/scroll-into-focus';
import type { MaterialLineInput } from '@shared/schemas';
import { useAuthStore } from '@client/stores/auth';
import { Badge, planTone } from '@ui/components/Badge';
import { Button } from '@ui/components/Button';
import { Card } from '@ui/components/Card';
import { PageHeader } from '@ui/components/PageHeader';
import { Row } from '@ui/components/Row';

type DraftLine = MaterialLineInput & { id: string; showDiscount: boolean };
type DraftItem = {
  id: string;
  itemName: string;
  materials: DraftLine[];
};

function basisPointsToPercent(bp: number): number {
  return Math.round(bp) / 100;
}

// Last-used rate keyed by `${materialId}__${unitId}`. When the user adds a new
// line we prefer their most recent rate for that pair (gold prices move daily —
// the catalog default is stale within hours of any rate update).
type LastRates = Readonly<Record<string, number>>;

function rateFor(
  material: CatalogMaterial,
  unitId: string,
  lastRates: LastRates | undefined,
): number {
  const key = `${material.id}__${unitId}`;
  const recent = lastRates?.[key];
  if (typeof recent === 'number' && Number.isFinite(recent) && recent >= 0) return recent;
  return material.defaultRatePaise ?? 0;
}

function makeLine(
  material: CatalogMaterial,
  units: ReadonlyArray<CatalogUnit>,
  lastRates?: LastRates,
): DraftLine {
  const unit = units.find((u) => u.id === material.defaultUnitId) ?? units[0];
  const unitId = unit?.id ?? '';
  return {
    id: crypto.randomUUID(),
    materialId: material.id,
    unitId,
    quantity: 0,
    ratePaise: rateFor(material, unitId, lastRates),
    discountPercent: 0,
    gstPercent: basisPointsToPercent(material.defaultGstBasisPoints),
    showDiscount: false,
  };
}

function makeItem(
  material: CatalogMaterial,
  units: ReadonlyArray<CatalogUnit>,
  lastRates?: LastRates,
): DraftItem {
  return {
    id: crypto.randomUUID(),
    itemName: '',
    materials: [makeLine(material, units, lastRates)],
  };
}

export function CreateEstimatePage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.me);
  const businessId = me?.business?.id ?? '';
  const isPro = me?.plan.level === 'pro' && me?.plan.state === 'active';
  const datalistId = useId();

  const materialsQuery = useQuery({
    queryKey: ['catalog', 'materials'],
    queryFn: fetchMaterials,
    staleTime: 5 * 60_000,
  });
  const unitsQuery = useQuery({
    queryKey: ['catalog', 'units'],
    queryFn: fetchUnits,
    staleTime: 5 * 60_000,
  });

  const materials = useMemo(() => materialsQuery.data?.materials ?? [], [materialsQuery.data]);
  const units = useMemo(() => unitsQuery.data?.units ?? [], [unitsQuery.data]);

  // Hybrid autofill source. localStorage cache is read synchronously in a
  // lazy state initializer so the form has lastRates + names the instant it
  // mounts (no first-paint flicker, no seed-timing race, works offline). The
  // server query refreshes the cache in the background — its onSuccess writes
  // through to localStorage so the next mount stays warm. Save mutations also
  // merge their just-typed rates/names locally, so a user editing a rate sees
  // it persist immediately without waiting for the next server roundtrip.
  const [cachedContext, setCachedContext] = useState(() => getCachedRecentContext(businessId));
  const recentContextQuery = useQuery({
    queryKey: ['estimates', 'recent-context', businessId],
    queryFn: fetchRecentContext,
    enabled: Boolean(businessId),
    // 5 min: balances freshness against re-fetch cost. Save mutation
    // invalidates this key explicitly so concurrent edits in the same shop
    // converge faster than the staleTime would otherwise allow.
    staleTime: 5 * 60_000,
  });
  // Reconcile server response into both local state + persistent cache. We
  // only depend on data here so this fires once per fresh server payload —
  // no per-render churn, no per-keystroke localStorage writes.
  useEffect(() => {
    if (!businessId) return;
    const data = recentContextQuery.data;
    if (!data) return;
    setCachedContext({
      itemNames: data.itemNames,
      lastRates: data.lastRates,
      updatedAt: Date.now(),
    });
    setCachedRecentContext(businessId, data);
  }, [businessId, recentContextQuery.data]);

  const lastRates = cachedContext?.lastRates;
  const serverItemNames = useMemo(() => cachedContext?.itemNames ?? [], [cachedContext]);

  const [items, setItems] = useState<DraftItem[]>([]);
  const [estimateDiscountPercent, setEstimateDiscountPercent] = useState(0);
  const [showEstimateDiscount, setShowEstimateDiscount] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerWhatsapp, setCustomerWhatsapp] = useState('');
  const [showCustomer, setShowCustomer] = useState(false);

  // Suggestions list merges localStorage cache (instant, prior saves on this
  // device) with server-backed names (covers fresh device / new origin where
  // localStorage is empty after the osunio.in domain migration). Local entries
  // win on dedupe so the user's most recent typing always shows up first.
  const itemNameSuggestions = useMemo(() => {
    const local = businessId ? getItemNameSuggestions(businessId) : [];
    const seen = new Set<string>();
    const merged: string[] = [];
    for (const name of [...local, ...serverItemNames]) {
      const k = name.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      merged.push(name);
    }
    return merged;
  }, [businessId, serverItemNames]);

  // Auto-seed first item with the warm cache rate. cachedContext was loaded
  // synchronously in useState, so lastRates is available on the very first
  // render — no async race like the previous query-only path had. If the
  // server fetch later returns a fresher rate (e.g. a colleague just saved a
  // higher gold rate), the user can re-pick the material from the dropdown
  // or click the per-line Reset to pull the new value.
  useEffect(() => {
    if (items.length > 0) return;
    if (materials.length === 0 || units.length === 0) return;
    const first = materials[0];
    if (first) setItems([makeItem(first, units, lastRates)]);
  }, [items.length, materials, units, lastRates]);

  const itemTotals = useMemo(() => {
    return items.map((item) => {
      try {
        return calculateItem({
          itemName: item.itemName || ' ',
          materials: item.materials.map(({ id: _id, showDiscount: _sd, ...rest }) => rest),
        });
      } catch {
        return { basePaise: 0, discountedPaise: 0, gstPaise: 0, itemTotalPaise: 0 };
      }
    });
  }, [items]);

  const totals = useMemo(() => {
    return calculateEstimate(
      items.map((item, idx) => ({
        id: item.id,
        itemName: item.itemName,
        materials: [],
        ...itemTotals[idx]!,
      })),
      estimateDiscountPercent,
    );
  }, [items, itemTotals, estimateDiscountPercent]);

  const saveMutation = useMutation({
    mutationFn: () =>
      saveEstimate({
        customerName: customerName.trim() || null,
        customerWhatsapp: customerWhatsapp.trim() || null,
        items: items.map((item) => ({
          itemName: item.itemName,
          materials: item.materials.map(({ id: _id, showDiscount: _sd, ...rest }) => rest),
        })),
        estimateDiscountPercent,
      }),
    onSuccess: async ({ estimate }) => {
      // Invalidates both the list (['estimates']) and the autofill context
      // (['estimates','recent-context', businessId]) so concurrent edits in
      // the same shop reconcile against the server.
      await queryClient.invalidateQueries({ queryKey: ['estimates'] });
      if (businessId) {
        // Optimistic local merge so the user does not have to wait for the
        // server roundtrip to see their just-typed rates and item names on
        // the next render. mergeIntoRecentContext keeps most-recent-first
        // ordering and dedupes case-insensitively. Server refetch will
        // overwrite this with the authoritative business-wide pool shortly.
        const justTypedNames = items.map((i) => i.itemName).filter((n) => n.trim().length > 0);
        const justTypedRates: Record<string, number> = {};
        for (const item of items) {
          for (const line of item.materials) {
            if (line.ratePaise > 0) {
              justTypedRates[`${line.materialId}__${line.unitId}`] = line.ratePaise;
            }
          }
        }
        mergeIntoRecentContext(businessId, {
          itemNames: justTypedNames,
          lastRates: justTypedRates,
        });
        // Refresh in-component view of the cache so the unmount → remount on
        // navigate(`/estimates/${id}`) is not the only path that picks up the
        // merge (covers scenarios where the user clicks New again from nav).
        setCachedContext(getCachedRecentContext(businessId));
        // Legacy single-purpose name cache kept in sync for any code path
        // that still reads it directly.
        rememberItemNames(businessId, justTypedNames);
      }
      setItems([]);
      setCustomerName('');
      setCustomerWhatsapp('');
      setEstimateDiscountPercent(0);
      setShowEstimateDiscount(false);
      setShowCustomer(false);
      navigate(`/estimates/${estimate.id}`);
    },
  });

  const updateItem = (id: string, patch: Partial<DraftItem>): void => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const updateLine = (itemId: string, lineId: string, patch: Partial<DraftLine>): void => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId
          ? {
              ...it,
              materials: it.materials.map((l) => (l.id === lineId ? { ...l, ...patch } : l)),
            }
          : it,
      ),
    );
  };

  const addLine = (itemId: string): void => {
    const first = materials[0];
    if (!first) return;
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId
          ? { ...it, materials: [...it.materials, makeLine(first, units, lastRates)] }
          : it,
      ),
    );
  };

  const removeLine = (itemId: string, lineId: string): void => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId ? { ...it, materials: it.materials.filter((l) => l.id !== lineId) } : it,
      ),
    );
  };

  const removeItem = (id: string): void => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const addItem = (): void => {
    const first = materials[0];
    if (!first) return;
    setItems((prev) => [...prev, makeItem(first, units, lastRates)]);
  };

  const planLabel = me?.plan.level ?? 'free';
  const planState = me?.plan.state ?? 'expired';

  return (
    <div className="space-y-5 pb-32 sm:pb-6">
      <PageHeader
        title="New estimate"
        subtitle="Tap to add materials. Totals update live."
        actions={
          <Badge tone={planTone(planState)}>
            {planLabel.toUpperCase()} · {planState}
          </Badge>
        }
      />

      <Card padding="md">
        {showCustomer ? (
          <>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Customer
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowCustomer(false)}>
                Hide
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-medium">Name</span>
                <input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  onFocus={(e) => scrollFocusedToUpperThird(e.target)}
                  placeholder="Walk-in customer"
                  autoComplete="name"
                  className="h-12 rounded-xl border border-slate-300 bg-white px-3 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-950 dark:focus:border-slate-100"
                />
              </label>
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-medium">WhatsApp</span>
                <input
                  value={customerWhatsapp}
                  onChange={(e) => setCustomerWhatsapp(e.target.value)}
                  onFocus={(e) => scrollFocusedToUpperThird(e.target)}
                  placeholder="+919876543210"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  className="h-12 rounded-xl border border-slate-300 bg-white px-3 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-950 dark:focus:border-slate-100"
                />
              </label>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setShowCustomer(true)}
            className="flex w-full items-center justify-between text-left text-sm text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
          >
            <span className="font-medium">+ Add customer (optional)</span>
            <span className="text-xs text-slate-400">name + WhatsApp</span>
          </button>
        )}
      </Card>

      {itemNameSuggestions.length > 0 ? (
        <datalist id={datalistId}>
          {itemNameSuggestions.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      ) : null}

      <div className="space-y-3">
        {items.map((item, idx) => (
          <ItemCard
            key={item.id}
            index={idx}
            item={item}
            materials={materials}
            units={units}
            {...(lastRates ? { lastRates } : {})}
            {...(itemNameSuggestions.length > 0 ? { datalistId } : {})}
            itemTotalPaise={itemTotals[idx]?.itemTotalPaise ?? 0}
            canRemove={items.length > 1}
            onItemNameChange={(name) => updateItem(item.id, { itemName: name })}
            onLineChange={(lineId, patch) => updateLine(item.id, lineId, patch)}
            onAddLine={() => addLine(item.id)}
            onRemoveLine={(lineId) => removeLine(item.id, lineId)}
            onRemoveItem={() => removeItem(item.id)}
          />
        ))}
        <button
          type="button"
          onClick={addItem}
          className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-slate-300 p-4 text-sm font-medium text-slate-600 transition hover:border-slate-400 hover:bg-slate-50 active:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <span aria-hidden="true">+</span> Add another item
        </button>
      </div>

      <Card padding="md">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          Summary
        </h2>
        <Row label="Subtotal" value={formatInr(totals.subtotalPaise)} />
        {showEstimateDiscount ? (
          <div className="mt-2">
            <NumericInput
              label="Overall discount %"
              value={estimateDiscountPercent}
              onValueChange={setEstimateDiscountPercent}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowEstimateDiscount(true)}
            className="mt-2 text-sm text-amber-700 underline-offset-2 hover:underline dark:text-amber-400"
          >
            + Add overall discount
          </button>
        )}
        {estimateDiscountPercent > 0 && (
          <Row
            label={`Discount (${estimateDiscountPercent}%)`}
            value={`− ${formatInr(totals.estimateDiscountPaise)}`}
          />
        )}
        <Row label="Total" value={formatInr(totals.totalPaise)} emphasis />
      </Card>

      <TotalBar
        totalPaise={totals.totalPaise}
        canSave={isPro && items.length > 0 && totals.totalPaise > 0}
        isPro={isPro}
        itemsCount={items.length}
        saving={saveMutation.isPending}
        error={saveMutation.error}
        onSave={() => saveMutation.mutate()}
      />
    </div>
  );
}

function ItemCard({
  index,
  item,
  materials,
  units,
  datalistId,
  lastRates,
  itemTotalPaise,
  canRemove,
  onItemNameChange,
  onLineChange,
  onAddLine,
  onRemoveLine,
  onRemoveItem,
}: {
  index: number;
  item: DraftItem;
  materials: ReadonlyArray<CatalogMaterial>;
  units: ReadonlyArray<CatalogUnit>;
  datalistId?: string;
  lastRates?: LastRates;
  itemTotalPaise: number;
  canRemove: boolean;
  onItemNameChange: (name: string) => void;
  onLineChange: (lineId: string, patch: Partial<DraftLine>) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: string) => void;
  onRemoveItem: () => void;
}): JSX.Element {
  return (
    <Card padding="md" as="article">
      <header className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {index + 1}
          </span>
          <h3 className="text-sm font-semibold tracking-tight">Item</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200">
            {formatInr(itemTotalPaise)}
          </span>
          {canRemove ? (
            <Button variant="ghost" size="sm" onClick={onRemoveItem}>
              Remove
            </Button>
          ) : null}
        </div>
      </header>

      <label className="mb-3 flex flex-col gap-1.5 text-sm">
        <span className="font-medium">
          Item name <span className="text-xs font-normal text-slate-400">(optional)</span>
        </span>
        <input
          value={item.itemName}
          onChange={(e) => onItemNameChange(e.target.value)}
          onFocus={(e) => scrollFocusedToUpperThird(e.target)}
          placeholder="e.g. 22K bridal necklace"
          {...(datalistId ? { list: datalistId } : {})}
          className="h-12 rounded-xl border border-slate-300 bg-white px-3 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-950 dark:focus:border-slate-100"
        />
      </label>

      <div className="space-y-3">
        {item.materials.map((line, lineIdx) => (
          <MaterialLine
            key={line.id}
            line={line}
            lineIndex={lineIdx}
            materials={materials}
            units={units}
            {...(lastRates ? { lastRates } : {})}
            canRemove={item.materials.length > 1}
            onChange={(patch) => onLineChange(line.id, patch)}
            onRemove={() => onRemoveLine(line.id)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={onAddLine}
        className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-400 hover:bg-slate-50 active:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <span aria-hidden="true">+</span> Add material (gold, stone, making…)
      </button>
    </Card>
  );
}

function MaterialLine({
  line,
  lineIndex,
  materials,
  units,
  lastRates,
  canRemove,
  onChange,
  onRemove,
}: {
  line: DraftLine;
  lineIndex: number;
  materials: ReadonlyArray<CatalogMaterial>;
  units: ReadonlyArray<CatalogUnit>;
  lastRates?: LastRates;
  canRemove: boolean;
  onChange: (patch: Partial<DraftLine>) => void;
  onRemove: () => void;
}): JSX.Element {
  const material = materials.find((m) => m.id === line.materialId);
  const lineCalc = (() => {
    try {
      return calculateMaterialLine({
        materialId: line.materialId,
        unitId: line.unitId,
        quantity: line.quantity,
        ratePaise: line.ratePaise,
        discountPercent: line.discountPercent,
        gstPercent: line.gstPercent,
      });
    } catch {
      return { lineTotalPaise: 0 };
    }
  })();

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-3 dark:border-slate-800 dark:bg-slate-900/40">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Material {lineIndex + 1}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium tabular-nums text-slate-600 dark:text-slate-300">
            {formatInr(lineCalc.lineTotalPaise)}
          </span>
          {canRemove ? (
            <button
              type="button"
              onClick={onRemove}
              className="text-xs text-red-600 underline-offset-2 hover:underline"
              aria-label={`Remove material ${lineIndex + 1}`}
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>

      {/* Mobile-friendly grid: Material spans both columns, then Unit+GST,
          Quantity+Rate, and the Discount row pair up two-up. The previous
          layout used `sm:grid-cols-2` (one column on mobile) which forced
          excessive scrolling on phones — every field was its own row. */}
      <div className="grid grid-cols-2 gap-3">
        <label className="col-span-2 flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Material</span>
          <select
            value={line.materialId}
            onChange={(e) => {
              const m = materials.find((x) => x.id === e.target.value);
              if (!m) return;
              onChange({
                materialId: m.id,
                unitId: m.defaultUnitId,
                gstPercent: basisPointsToPercent(m.defaultGstBasisPoints),
                ratePaise: rateFor(m, m.defaultUnitId, lastRates),
              });
            }}
            className="h-12 rounded-xl border border-slate-300 bg-white px-3 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-950 dark:focus:border-slate-100"
          >
            {materials.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Unit</span>
          <select
            value={line.unitId}
            onChange={(e) => {
              const newUnitId = e.target.value;
              // Switching units changes the rate semantics (₹/g vs ₹/tola).
              // Pull the most-recent rate for the new (material,unit) pair if
              // we have one, otherwise leave the user's typed rate alone.
              const recentKey = `${line.materialId}__${newUnitId}`;
              const recentRate = lastRates?.[recentKey];
              onChange({
                unitId: newUnitId,
                ...(typeof recentRate === 'number' && Number.isFinite(recentRate) && recentRate >= 0
                  ? { ratePaise: recentRate }
                  : {}),
              });
            }}
            className="h-12 rounded-xl border border-slate-300 bg-white px-3 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-950 dark:focus:border-slate-100"
          >
            {units
              .filter((u) => u.category !== 'purity')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </select>
        </label>
        {/* DOM order chosen to match the visual grid: Material (full),
            Unit | GST, Quantity | Rate, Discount (full). Reordering here
            instead of fighting CSS grid flow — `grid-flow-row` already
            does what we want once siblings are in the right order. */}
        <NumericInput
          label="GST %"
          value={line.gstPercent}
          onValueChange={(v) => onChange({ gstPercent: v })}
          decimal
          modified={
            !!material && line.gstPercent !== basisPointsToPercent(material.defaultGstBasisPoints)
          }
          {...(material
            ? {
                onReset: () =>
                  onChange({ gstPercent: basisPointsToPercent(material.defaultGstBasisPoints) }),
              }
            : {})}
        />
        <NumericInput
          label="Quantity"
          value={line.quantity}
          onValueChange={(v) => onChange({ quantity: v })}
          decimal
          placeholder="0"
        />
        <NumericInput
          label="Rate (₹/unit)"
          value={line.ratePaise / 100}
          onValueChange={(v) => onChange({ ratePaise: Math.round(v * 100) })}
          decimal
          modified={!!material?.defaultRatePaise && line.ratePaise !== material.defaultRatePaise}
          {...(material?.defaultRatePaise
            ? { onReset: () => onChange({ ratePaise: material.defaultRatePaise ?? 0 }) }
            : {})}
        />
        {line.showDiscount ? (
          <div className="col-span-2">
            <NumericInput
              label="Discount %"
              value={line.discountPercent}
              onValueChange={(v) => onChange({ discountPercent: v })}
              decimal
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onChange({ showDiscount: true })}
            className="col-span-2 text-left text-sm text-amber-700 underline-offset-2 hover:underline dark:text-amber-400"
          >
            + Add discount
          </button>
        )}
      </div>
    </div>
  );
}

function TotalBar({
  totalPaise,
  canSave,
  isPro,
  itemsCount,
  saving,
  error,
  onSave,
}: {
  totalPaise: number;
  canSave: boolean;
  isPro: boolean;
  itemsCount: number;
  saving: boolean;
  error: unknown;
  onSave: () => void;
}): JSX.Element {
  // Hide while keypad is open — keypad lives at z-50 and a sticky total bar
  // would otherwise overlap the keypad's bottom row.
  const keypadOpen = useKeypadOverlayStore((s) => s.isOpen);
  // Build the disabled-state explanation so a user understands why "Save"
  // is greyed. Multiple causes exist (no Pro, no items, zero total) — pick
  // the one that's currently true and surface it as a tiny hint.
  const disabledReason = !isPro
    ? 'Saving needs the Pro plan'
    : itemsCount === 0
      ? 'Add at least one item to save'
      : totalPaise === 0
        ? 'Add a quantity and rate to enable save'
        : null;

  if (keypadOpen) return <></>;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:static sm:inset-auto sm:border-0 sm:bg-transparent sm:backdrop-blur-none"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
    >
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-4 px-4 pt-3 sm:px-8 sm:pt-0">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">Total</p>
          <p className="text-xl font-bold tabular-nums">{formatInr(totalPaise)}</p>
        </div>
        <Button
          size="lg"
          disabled={!canSave}
          pending={saving}
          pendingLabel="Saving…"
          onClick={onSave}
          {...(disabledReason ? { title: disabledReason } : {})}
          aria-describedby={disabledReason ? 'save-hint' : undefined}
        >
          Save estimate
        </Button>
      </div>
      {disabledReason ? (
        <p
          id="save-hint"
          className="mx-auto mt-1 max-w-5xl px-4 text-right text-xs text-slate-500 sm:px-8"
        >
          {disabledReason}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="mx-auto mt-2 max-w-5xl px-4 text-center text-sm text-red-600 sm:px-8"
        >
          {error instanceof ApiError ? error.message : 'Save failed'}
        </p>
      ) : null}
    </div>
  );
}
